import React, { useMemo } from 'react';
import { ElementKind, OneOfListElements } from '@shared/models/project.model';
import {
  toggleElementExpanded,
  useIsElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { getElementKind } from '@shared/helpers/recursive-element.helpers';
import { isProduct } from '@shared/helpers/product.helpers';
import { isProjectInfoOrFolder } from '@shared/helpers/project-folder.helpers';
import { clearSelectionForCollapsedDescendants } from '@client/hooks/element-list-collapse.helpers';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { IconButton } from '@ui/buttons';

interface ExpandIconData {
  element: OneOfListElements;
  disabled?: boolean;
  hidden?: boolean;
}

export const ExpandButton: React.FC<ExpandIconData> = ({
  element,
  disabled,
  hidden,
}) => {
  const navigateTo = useProjectNavigation();

  const kind = getElementKind(element);
  const expanded = useIsElementExpanded(element);
  const isProject = isProjectInfoOrFolder(element);

  const handleClick = useMouseEventCallback(
    () => {
      if (expanded) {
        clearSelectionForCollapsedDescendants(element, navigateTo);
      }
      toggleElementExpanded(element, isProject);
    },
    { stopPropagation: true },
  );

  return useMemo(() => {
    const arrowRotation = expanded ? 0 : -90;
    const arrowIcon = (
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        category="mui"
        icon="ArrowDropDown"
        rotateIcon={arrowRotation}
        shape="square"
        hidden={hidden}
      />
    );
    if (isProduct(element)) {
      return arrowIcon;
    }

    switch (kind) {
      case ElementKind.ProjectFolder:
        return (
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            category="mui"
            icon={expanded ? 'FolderOpen' : 'Folder'}
            shape="square"
            hidden={hidden}
          />
        );

      case ElementKind.Product:
        return (
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            category="ui"
            icon="categoryProduct"
            shape="square"
            hidden={hidden}
          />
        );

      case ElementKind.ProductCategory:
        return arrowIcon;

      case ElementKind.ProductGroup:
        return arrowIcon;

      case ElementKind.Element:
        // Use a small triangle icon for all expandable elements (per redesign)
        return arrowIcon;

      default:
        return (
          <IconButton
            onClick={() => undefined}
            category="mui"
            icon="ArrowDropDown"
            shape="square"
            hidden
            disabled
          />
        );
    }
  }, [element, kind, expanded, disabled, handleClick, hidden]);
};
